
import React, { useEffect, useState } from 'react';

interface OneSignalProviderProps {
  children: React.ReactNode;
}

// Singleton per prevenire inizializzazioni multiple
let oneSignalInitialized = false;
let initializationPromise: Promise<boolean> | null = null;

/**
 * OneSignalProvider ottimizzato per prevenire conflitti
 * 
 * Utilizza un singleton pattern per garantire una sola inizializzazione
 * e previene i conflitti con altre funzionalità dell'app.
 */
export const OneSignalProvider: React.FC<OneSignalProviderProps> = ({ children }) => {
  const [initState, setInitState] = useState<'idle' | 'initializing' | 'success' | 'failed'>('idle');

  useEffect(() => {
    // Skip initialization in SSR
    if (typeof window === 'undefined') {
      console.log("[OneSignalProvider] Skipping initialization in SSR");
      return;
    }

    // Se già inizializzato, non fare nulla
    if (oneSignalInitialized) {
      setInitState('success');
      return;
    }

    // Se inizializzazione in corso, attendi
    if (initializationPromise) {
      initializationPromise.then(success => {
        setInitState(success ? 'success' : 'failed');
      }).catch(() => {
        setInitState('failed');
      });
      return;
    }

    // Avvia inizializzazione solo se necessario
    if (initState === 'idle') {
      initializeOneSignal();
    }
  }, [initState]);

  const initializeOneSignal = async () => {
    if (oneSignalInitialized || initializationPromise) {
      return;
    }

    setInitState('initializing');
    console.log("[OneSignalProvider] Starting OneSignal initialization");

    // Crea la promise di inizializzazione
    initializationPromise = new Promise(async (resolve) => {
      try {
        // Import dinamico per evitare blocchi
        const { edgeOnlyNotificationService } = await import('@/services/notifications/edgeOnlyNotificationService');
        
        // Inizializzazione con timeout
        const success = await Promise.race([
          edgeOnlyNotificationService.initialize(),
          new Promise<boolean>((_, reject) => 
            setTimeout(() => reject(new Error('Initialization timeout')), 10000)
          )
        ]);
        
        if (success) {
          oneSignalInitialized = true;
          console.log("[OneSignalProvider] OneSignal initialized successfully");
          setInitState('success');
          resolve(true);
        } else {
          console.warn("[OneSignalProvider] OneSignal initialization failed");
          setInitState('failed');
          resolve(false);
        }
      } catch (error) {
        console.error("[OneSignalProvider] Initialization error:", error);
        setInitState('failed');
        resolve(false);
      }
    });

    await initializationPromise;
  };

  // Cleanup dell'inizializzazione al unmount
  useEffect(() => {
    return () => {
      // Reset solo se il componente viene completamente smontato
      // Non durante i re-render normali
    };
  }, []);

  return <>{children}</>;
};
